import { Control, Controller, UseFormResetField } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { MultiSelectPanelHeaderTemplateParams } from 'primereact/multiselect'

// local imports
import { IHealthLocationList } from 'types/HealthType'
import { GearFuelTypeOptions, IFilteredPoolVehicleList, IPoolVehicleSearch } from 'types/PoolVehicleType'
import { StyledMultiSelect } from 'components/map/styled'
import {
  DropdownWrapper,
  StyledCustomDropdown,
} from 'components/home/on-campus-services/coiffeur/coiffeur-search-modal/styled'

interface IPoolVehicleFormProps {
  control: Control<IPoolVehicleSearch>
  resetField: UseFormResetField<IPoolVehicleSearch>
  locationList: IHealthLocationList[] | undefined
  vehicleList: IFilteredPoolVehicleList[] | []
  gearTypes: GearFuelTypeOptions[] | []
  fuelTypes: GearFuelTypeOptions[] | []
  isDisabled: boolean
}

function valueTemplate(options: any, props: any) {
  return <div className="selectedVal">{options?.name || props?.placeholder}</div>
}

function vehicleValueTemplate(options: IFilteredPoolVehicleList, props: any) {
  const optionText = !options
    ? ''
    : `${options?.brandOfVehicle || ''} - ${options?.modelOfVehicle || ''} - ${options?.gearType?.name || ''} - ${
        options?.fuelType?.name || ''
      }`

  return <div className="selectedVal">{optionText || props?.placeholder}</div>
}

export default function PoolVehicleSearchForm({
  control,
  resetField,
  vehicleList,
  locationList,
  fuelTypes,
  gearTypes,
  isDisabled,
}: IPoolVehicleFormProps) {
  const { t } = useTranslation()
  const vehicleOptions = vehicleList?.map((vehicle) => ({
    ...vehicle,
    optionLabel: `${vehicle?.brandOfVehicle} - ${vehicle?.modelOfVehicle} - ${vehicle?.gearType?.name} - ${vehicle?.fuelType?.name}`,
  }))

  const plateNumber = control._getWatch('vehicle')?.plate

  function panelHeaderTemplate(options: MultiSelectPanelHeaderTemplateParams) {
    return (
      <div className="p-multiselect-header">
        <label className="d-flex align-items-center">
          {options.checkboxElement}
          <span role="button" className="px-2 user-select-none">
            {t('buttons.choose-all')}
          </span>
        </label>
      </div>
    )
  }

  return (
    <div className="d-flex flex-column gap-2">
      {/* Location */}
      <Controller
        name="locationId"
        control={control}
        render={({ field }) => (
          <DropdownWrapper>
            <label className="autocomplete-title">{t('most-used-words.location')}</label>
            <i className="autocomplete-icon p-input-icon-left pi pi-map-marker"></i>
            <StyledCustomDropdown
              id="poolVehicleLocation"
              emptyMessage={t('most-used-words.option-not-found')}
              emptyFilterMessage={t('most-used-words.option-not-found')}
              name="locationId"
              placeholder={t('input-texts.choose-location')}
              optionLabel="name"
              optionValue="id"
              valueTemplate={(opt, props) => valueTemplate(opt, props)}
              options={locationList}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.value)
                resetField('vehicle')
              }}
            />
          </DropdownWrapper>
        )}
      />

      {/* Gear & Fuel Types */}
      <Controller
        name="gearType"
        control={control}
        render={({ field }) => (
          <DropdownWrapper>
            <label className="autocomplete-title">{t('input-texts.gear-type')}</label>
            <i className="autocomplete-icon p-input-icon-left pi pi-sitemap"></i>
            <StyledCustomDropdown
              {...field}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.value)
                resetField('vehicle')
              }}
              valueTemplate={(opt, props) => valueTemplate(opt, props)}
              options={gearTypes}
              optionLabel="name"
              optionValue="id"
              placeholder={t('input-texts.gear-type')}
            />
          </DropdownWrapper>
        )}
      />

      <Controller
        name="fuelTypes"
        control={control}
        render={({ field }) => (
          <StyledMultiSelect
            id={field.name}
            {...field}
            value={field.value}
            optionLabel="name"
            optionValue="id"
            name={field.name}
            options={fuelTypes}
            onChange={(e) => {
              field.onChange(e.value)
              resetField('vehicle')
            }}
            placeholder={t('input-texts.fuel-type')}
            panelHeaderTemplate={panelHeaderTemplate}
            maxSelectedLabels={2}
            selectedItemsLabel={t('most-used-words.all-selected')}
          />
        )}
      />

      {/* Pool Vehicle */}
      <Controller
        name="vehicle"
        control={control}
        render={({ field }) => (
          <DropdownWrapper>
            <label className="autocomplete-title">{t('input-texts.pool-vehicle')}</label>
            <StyledCustomDropdown
              disabled={isDisabled}
              id="poolVehicleDropdown"
              emptyMessage={t('most-used-words.option-not-found')}
              emptyFilterMessage={t('most-used-words.option-not-found')}
              name="vehicle"
              placeholder={t('input-texts.choose-pool-vehicle')}
              optionLabel="optionLabel"
              options={vehicleOptions}
              value={field.value}
              valueTemplate={(opt, props) => vehicleValueTemplate(opt, props)}
              onChange={(e) => {
                field.onChange(e.value)
              }}
            />
          </DropdownWrapper>
        )}
      />

      {!!plateNumber && (
        <div className="mt-2">
          <span className="font-bold ">{t('pool-vehicle-res-modal.license-plate')}: </span> <span> {plateNumber}</span>
        </div>
      )}
    </div>
  )
}
